// Contact tracker view helpers: dates, the contact form and the batch log. The queue and the
// contact log have their own (queueView.js, logView.js).

export const PAGE_SIZE = 50;
export const MAX_NOTE_LENGTH = 4000;

// Times from the API are zoneless UTC ("2026-10-20T00:00:00Z" or without the Z).
const asUTC = (iso) => {
  if (!iso) return null;
  const s = String(iso);
  const t = Date.parse(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(t) ? null : new Date(t);
};

export const formatDay = (iso) => {
  const d = asUTC(iso);
  return d ? d.toISOString().slice(0, 10) : '—';
};

// daysAgo counts whole UTC calendar days between iso and now.
export const daysAgo = (iso, now = new Date()) => {
  const d = asUTC(iso);
  if (!d) return null;
  const day = (x) => Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
  return Math.max(0, Math.round((day(now) - day(d)) / 86400000));
};

export const agoLabel = (iso, now = new Date()) => {
  const n = daysAgo(iso, now);
  if (n === null) return '—';
  if (n === 0) return 'today';
  if (n === 1) return 'yesterday';
  return `${n}d ago`;
};

export const todayISO = (now = new Date()) => now.toISOString().slice(0, 10);

const DATE = /^\d{4}-\d{2}-\d{2}$/;

// contactFormErrors checks the form as the backend will; an empty list means it can be saved.
export const contactFormErrors = ({ channelId, contactedAt, nextContactAt, note }, now = new Date()) => {
  const errors = [];
  if (!channelId) errors.push('Choose a channel.');
  if (!(note || '').trim()) errors.push('Add a note.');
  if (!DATE.test(contactedAt || '')) errors.push('Pick the contact date.');
  else if (contactedAt > todayISO(now)) errors.push('The contact date cannot be in the future.');
  if (nextContactAt && (!DATE.test(nextContactAt) || nextContactAt < contactedAt)) {
    errors.push('Follow-up cannot be before the contact date.');
  }
  if ((note || '').length > MAX_NOTE_LENGTH) errors.push(`The note is longer than ${MAX_NOTE_LENGTH} characters.`);
  return errors;
};

export const contactBody = ({ channelId, contactedAt, nextContactAt, note }) => {
  const body = { channel_id: Number(channelId), contacted_at: contactedAt };
  if (nextContactAt) body.next_contact_at = nextContactAt;
  const trimmed = (note || '').trim();
  if (trimmed) body.note = trimmed;
  return body;
};

export const apiError = (err, fallback) => err?.response?.data?.error || fallback;

// Page tabs; the log (?tab=log, with its own filters) and the batch form (?tab=batch) can be linked.
export const TABS = [
  { key: 'queue', label: 'Queue' },
  { key: 'log', label: 'Contact log' },
  { key: 'batch', label: 'Batch log' },
];
const TAB_KEYS = TABS.map((t) => t.key);
export const tabFromSearch = (searchParams) => {
  const tab = searchParams.get('tab');
  return TAB_KEYS.includes(tab) ? tab : 'queue';
};

// splitIdentifiers turns a pasted list (lines, commas, semicolons or spaces) into entries.
export const splitIdentifiers = (text) => (text || '').split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);

export const batchBody = (form, userIds) => ({ ...contactBody(form), user_ids: userIds });

const dayFromISO = (ymd) => (DATE.test(ymd || '') ? new Date(`${ymd}T00:00:00Z`) : null);

// formatShortDay: "12 Oct" (history entries).
export const formatShortDay = (iso) => {
  const d = asUTC(iso);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '—';
};

// formatWeekday: "Wed 5 Nov" (follow-up hint).
export const formatWeekday = (ymd) => {
  const d = dayFromISO(ymd);
  return d ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
};

// saveHint names what is still missing before the form can be saved.
export const saveHint = (form, now = new Date()) => {
  const noChannel = !form.channelId;
  const noNote = !(form.note || '').trim();
  if (noChannel && noNote) return 'Pick a channel and add a note to save.';
  if (noChannel) return 'Pick a channel to save.';
  if (noNote) return 'Add a note to save.';
  const errors = contactFormErrors(form, now);
  return errors.length > 0 ? errors[0] : 'Ready to save.';
};

// A follow-up dated today (or earlier) is due at once: the player stays on the due list.
const followUpToday = (ymd) => ymd && ymd <= todayISO();

export const followUpHint = (nextContactAt, who) => (nextContactAt
  ? (followUpToday(nextContactAt)
    ? `Due today. ${who} stays on the due list.`
    : `${who} returns to the due list on ${formatWeekday(nextContactAt)}. Click the selected option again to clear it.`)
  : `No follow-up: ${who} won't come back to the due list.`);


const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export const playersLabel = (n) => plural(n, 'player', 'players');

// parseBatchEntries: the pasted list, lowercased and deduplicated in input order.
export const parseBatchEntries = (text) => {
  const seen = new Set();
  const entries = [];
  let duplicates = 0;
  splitIdentifiers(text).forEach((raw) => {
    const v = raw.toLowerCase();
    if (seen.has(v)) duplicates += 1;
    else { seen.add(v); entries.push(v); }
  });
  return { entries, duplicates };
};

export const batchSummary = ({ entries, duplicates }) => {
  const base = plural(entries.length, 'entry', 'entries');
  return duplicates ? `${base} · ${plural(duplicates, 'duplicate', 'duplicates')} removed` : base;
};

export const BATCH_STATUS = { ok: 'Matched', missing: 'Not found', invalid: 'Not an email or ID' };

// batchRows pairs each checked entry with its result (the backend decides what is invalid or
// missing); problem rows come first. excluded entries were removed by the manager.
export const batchRows = (entries, resolved, excluded = []) => {
  if (!resolved) return [];
  const invalid = new Set((resolved.invalid || []).map((v) => v.toLowerCase()));
  const missing = new Set((resolved.not_found || []).map((v) => v.toLowerCase()));
  const byKey = new Map();
  (resolved.players || []).forEach((p) => {
    byKey.set(String(p.user_id), p);
    if (p.email) byKey.set(p.email.toLowerCase(), p);
  });
  const skip = new Set(excluded);
  const rows = entries.filter((v) => !skip.has(v)).map((value) => {
    const player = byKey.get(value);
    let status = 'ok';
    if (invalid.has(value)) status = 'invalid';
    else if (missing.has(value) || !player) status = 'missing';
    return { value, status, player: status === 'ok' ? player : null };
  });
  return [...rows.filter((r) => r.status !== 'ok'), ...rows.filter((r) => r.status === 'ok')];
};

// batchUserIds: the matched players, once each (an email and an ID may name the same player).
export const batchUserIds = (rows) => [...new Set(rows.filter((r) => r.player).map((r) => r.player.user_id))];

export const batchHint = ({ entries, checked, rows, form }) => {
  const matched = rows.filter((r) => r.status === 'ok').length;
  const bad = rows.length - matched;
  const noChannel = !form.channelId;
  const noNote = !(form.note || '').trim();
  if (entries.length === 0) return 'Paste players on the left to start.';
  if (!checked) return 'Check players before logging.';
  if (matched === 0) return 'No matched players to log.';
  if (noChannel && noNote) return 'Pick a channel and add a note.';
  if (noChannel) return 'Pick a channel.';
  if (noNote) return 'Add a note.';
  const errors = contactFormErrors(form);
  if (errors.length > 0) return errors[0];
  if (bad > 0) return `${plural(bad, 'unmatched entry is', 'unmatched entries are')} skipped.`;
  return 'Ready to log.';
};

export const batchFollowUpHint = (nextContactAt, n) => (nextContactAt
  ? (followUpToday(nextContactAt)
    ? `Due today for all ${playersLabel(n)}. They stay on the due list.`
    : `All ${playersLabel(n)} return to the due list on ${formatWeekday(nextContactAt)}.`)
  : "No follow-up: they won't come back to the due list.");
